import { Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { useAction } from "convex/react";
import { api } from "../../convex/_generated/api";

const Auth = () => {
  const {
    user,
    isAuthenticated,
    signIn,
    signUp,
    resetPassword,
    updatePassword,
    verifyEmail,
    resendVerificationEmail,
  } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const sendVerificationEmailAction = useAction(api.sendEmail.sendVerificationEmail);
  const [loading, setLoading] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignUpConfirmPassword, setShowSignUpConfirmPassword] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [showUpdateConfirmPassword, setShowUpdateConfirmPassword] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showPasswordUpdate, setShowPasswordUpdate] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [betaAcknowledged, setBetaAcknowledged] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [resetCode, setResetCode] = useState<string | null>(null);
  const [signInError, setSignInError] = useState<string>("");
  const [showSignUp, setShowSignUp] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState<string>("");
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");

    if (location.pathname === "/login/reset-password") {
      setShowPasswordUpdate(true);
      setResetCode(code);
    } else {
      setShowPasswordUpdate(false);
      setResetCode(null);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Always default to hidden password fields when auth mode changes.
    setShowSignInPassword(false);
    setShowSignUpPassword(false);
    setShowSignUpConfirmPassword(false);
    setShowUpdatePassword(false);
    setShowUpdateConfirmPassword(false);
  }, []);

  // CAPS LOCK detection
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (!e.getModifierState("CapsLock")) {
      setCapsLockOn(false);
    }
  };

  // Redirect if already authenticated, but allow password reset flow
  if ((user || isAuthenticated) && !showPasswordUpdate) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleTestEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!testEmail) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter an email address to test.",
      });
      return;
    }

    setTestEmailLoading(true);
    try {
      const baseUrl = window.location.origin;
      await sendVerificationEmailAction({
        email: testEmail,
        verificationUrl: `${baseUrl}/auth/verify?code=test-code-12345`,
      });
      toast({
        title: "Test email sent!",
        description: `Check ${testEmail} for the test verification email.`,
      });
      setTestEmail("");
      setShowTestEmail(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to send email",
        description: error instanceof Error ? error.message : "Check your SMTP configuration",
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setSignInError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { error } = await signIn(email, password);

    if (error) {
      setSignInError("password-mismatch");
    } else {
      navigate(redirectPath, { replace: true });
    }

    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;
    const fullName = formData.get("fullName") as string;

    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password mismatch",
        description: "Passwords do not match. Please try again.",
      });
      setLoading(false);
      return;
    }

    if (!betaAcknowledged) {
      toast({
        variant: "destructive",
        title: "Beta acknowledgment required",
        description:
          "Please acknowledge that you understand you are signing up for the beta version of 360Crafter.",
      });
      setLoading(false);
      return;
    }

    const { error } = await signUp(email, password, fullName);

    if (error) {
      if (error.message.includes("already registered")) {
        toast({
          variant: "destructive",
          title: "Account exists",
          description: "An account with this email already exists. Please sign in instead.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Sign up failed",
          description: error.message,
        });
      }
    } else {
      // Show email verification screen instead of toast
      setUserEmail(email);
      setShowEmailVerification(true);
    }

    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("resetEmail") as string;

    const { error } = await resetPassword(email);

    if (error) {
      toast({
        variant: "destructive",
        title: "Reset failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Reset email sent",
        description: "Check your email for password reset instructions.",
      });
    }

    setLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const password = formData.get("newPassword") as string;
    const confirmPassword = formData.get("confirmNewPassword") as string;

    if (password !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Password mismatch",
        description: "Passwords do not match. Please try again.",
      });
      setLoading(false);
      return;
    }

    const { error } = await updatePassword(password, resetCode ?? undefined);

    if (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated.",
      });
      // Redirect to dashboard after successful password update
      navigate("/dashboard");
    }

    setLoading(false);
  };

  const handleResendVerification = async () => {
    setLoading(true);

    const { error } = await resendVerificationEmail(userEmail);

    if (error) {
      toast({
        variant: "destructive",
        title: "Resend failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Verification email sent",
        description: "Please check your email and spam folder for the verification code.",
      });
    }

    setLoading(false);
  };

  const handleVerifyEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      toast({
        variant: "destructive",
        title: "Verification code required",
        description: "Enter the verification code from your email.",
      });
      return;
    }

    setVerificationLoading(true);
    const { error } = await verifyEmail(userEmail, verificationCode.trim());

    if (error) {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: error.message,
      });
    } else {
      toast({
        title: "Email verified",
        description: "Your email has been verified. You can sign in now.",
      });
      setShowEmailVerification(false);
      setVerificationCode("");
    }

    setVerificationLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">360Crafter</h1>
        </div>

        {!showEmailVerification && (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardHeader>
              {showPasswordUpdate ? (
                <>
                  <CardTitle className="text-white">Update Password</CardTitle>
                  <CardDescription className="text-blue-200">
                    Enter your new password below.
                  </CardDescription>
                </>
              ) : showReset ? (
                <>
                  <CardTitle className="text-white">Reset Password</CardTitle>
                  <CardDescription className="text-blue-200">
                    Enter your email to receive password reset instructions.
                  </CardDescription>
                </>
              ) : (
                <CardTitle className="text-white">Log in to your account</CardTitle>
              )}
            </CardHeader>
            <CardContent>
              {showPasswordUpdate ? (
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-white">
                      New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type={showUpdatePassword ? "text" : "password"}
                        required
                        autoComplete="new-password"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/60 pr-10"
                        placeholder="Enter your new password"
                        onKeyDown={handleKeyDown}
                        onKeyUp={handleKeyUp}
                      />
                      <button
                        type="button"
                        onClick={() => setShowUpdatePassword(!showUpdatePassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
                      >
                        {showUpdatePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {capsLockOn && (
                      <div className="text-amber-400 text-sm flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        CAPS LOCK is on
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmNewPassword" className="text-white">
                      Confirm New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        type={showUpdateConfirmPassword ? "text" : "password"}
                        required
                        autoComplete="new-password"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/60 pr-10"
                        placeholder="Confirm your new password"
                        onKeyDown={handleKeyDown}
                        onKeyUp={handleKeyUp}
                      />
                      <button
                        type="button"
                        onClick={() => setShowUpdateConfirmPassword(!showUpdateConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
                      >
                        {showUpdateConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {capsLockOn && (
                      <div className="text-amber-400 text-sm flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        CAPS LOCK is on
                      </div>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 !text-white"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Update Password
                  </Button>
                </form>
              ) : !showReset ? (
                showSignUp ? (
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-white">
                        Full Name
                      </Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required
                        autoComplete="name"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signupEmail" className="text-white">
                        Email
                      </Label>
                      <Input
                        id="signupEmail"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signupPassword" className="text-white">
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="signupPassword"
                          name="password"
                          type={showSignUpPassword ? "text" : "password"}
                          required
                          autoComplete="new-password"
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/60 pr-10"
                          placeholder="Create a password"
                          onKeyDown={handleKeyDown}
                          onKeyUp={handleKeyUp}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
                        >
                          {showSignUpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {capsLockOn && (
                        <div className="text-amber-400 text-sm flex items-center gap-2">
                          <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                          CAPS LOCK is on
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-white">
                        Confirm Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showSignUpConfirmPassword ? "text" : "password"}
                          required
                          autoComplete="new-password"
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/60 pr-10"
                          placeholder="Confirm your password"
                          onKeyDown={handleKeyDown}
                          onKeyUp={handleKeyUp}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignUpConfirmPassword(!showSignUpConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
                        >
                          {showSignUpConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      {capsLockOn && (
                        <div className="text-amber-400 text-sm flex items-center gap-2">
                          <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                          CAPS LOCK is on
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="betaAcknowledgment"
                          checked={betaAcknowledged}
                          onCheckedChange={(checked) => setBetaAcknowledged(checked as boolean)}
                          className="mt-1"
                        />
                        <Label
                          htmlFor="betaAcknowledgment"
                          className="text-white text-sm leading-relaxed cursor-pointer"
                        >
                          I understand that I am signing up for the beta version of 360Crafter.
                          This is a pre-release version that may contain bugs or incomplete
                          features. You can share bugs or missing features through our community
                          at community.360creators.com in the Early Adopters space.
                        </Label>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 !text-white"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Sign Up
                    </Button>
                    <div className="text-center pt-2 text-sm text-white">
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => setShowSignUp(false)}
                        className="text-blue-300 hover:underline"
                      >
                        Sign in
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white">
                          Email
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          autoComplete="username"
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                          placeholder="Enter your email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-white">
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="password"
                            name="password"
                            type={showSignInPassword ? "text" : "password"}
                            required
                            autoComplete="current-password"
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/60 pr-10"
                            placeholder="Enter your password"
                            onKeyDown={handleKeyDown}
                            onKeyUp={handleKeyUp}
                            onChange={() => setSignInError("")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowSignInPassword(!showSignInPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white"
                          >
                            {showSignInPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        {signInError && (
                          <div className="text-red-400 text-xs">
                            Check the account information you entered and try again.{" "}
                            <button
                              type="button"
                              onClick={() => setShowReset(true)}
                              className="text-blue-300 hover:underline"
                            >
                              Forgotten your password?
                            </button>
                          </div>
                        )}
                        {capsLockOn && (
                          <div className="text-amber-400 text-sm flex items-center gap-2">
                            <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                            CAPS LOCK is on
                          </div>
                        )}
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 !text-white"
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Continue
                      </Button>
                      <div className="text-center pt-2 text-sm text-white">
                        Don't have an account?{" "}
                        <button
                          type="button"
                          onClick={() => setShowSignUp(true)}
                          className="text-blue-300 hover:underline"
                        >
                          Sign up
                        </button>
                      </div>
                    </form>
                  </>
                )
              ) : (
                <>
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="resetEmail" className="text-white">
                        Email
                      </Label>
                      <Input
                        id="resetEmail"
                        name="resetEmail"
                        type="email"
                        required
                        autoComplete="email"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                        placeholder="Enter your email"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 !text-white"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Reset Password
                    </Button>
                  </form>
                  <div className="text-center mt-2">
                    <button
                      type="button"
                      className="text-blue-200 hover:underline text-sm"
                      onClick={() => setShowReset(false)}
                    >
                      Back to Sign In
                    </button>
                  </div>
                </>
              )}
              {isElectron() && !showPasswordUpdate && (
                <div className="pt-4">
                  <Button
                    variant="outline"
                    className="w-full border-white/30 text-white hover:bg-white/10"
                    onClick={() => navigate(redirectPath, { replace: true })}
                  >
                    Continue without login
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Test Email Button - appears when not showing other screens */}
        {!showEmailVerification && !showReset && !showPasswordUpdate && !showTestEmail && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setShowTestEmail(true)}
              className="text-blue-300 hover:text-blue-200 text-xs underline"
            >
              Test SMTP (Admin)
            </button>
          </div>
        )}

        {/* Test Email Form */}
        {showTestEmail && (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20 mt-4">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail size={18} />
                Test Email
              </CardTitle>
              <CardDescription className="text-blue-200">
                Send a test verification email to verify your SMTP setup
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleTestEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="testEmail" className="text-white">
                    Email Address
                  </Label>
                  <Input
                    id="testEmail"
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={testEmailLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {testEmailLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Test Email"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTestEmail(false)}
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Email Verification Screen */}
        {showEmailVerification && (
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-center">Verify Your Email</CardTitle>
              <CardDescription className="text-blue-200 text-center">
                We've sent a verification code to your email address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <title>Email Icon</title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-white mb-2">
                  Please check your email at <strong className="text-blue-300">{userEmail}</strong>
                </p>
                <p className="text-blue-200 text-sm mb-4">
                  Enter the verification code from the email to activate your account.
                </p>
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 mb-4">
                  <p className="text-amber-200 text-sm">
                    <strong>Didn't receive the email?</strong> Check your spam folder or junk mail
                    folder.
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerifyEmail} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="verificationCode" className="text-white">
                    Verification Code
                  </Label>
                  <Input
                    id="verificationCode"
                    name="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter the code"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={verificationLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {verificationLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify Email"
                  )}
                </Button>
              </form>

              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Resend Verification Code"
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setShowEmailVerification(false)}
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Back to Sign In
                </Button>
              </div>

              <div className="text-center pt-4 border-t border-white/10">
                <p className="text-blue-200 text-sm">
                  Need help? Contact us at{" "}
                  <a
                    href="https://360crafter.com/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-blue-200 underline"
                  >
                    360crafter.com/contact
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Auth;
